import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Car, Truck, Pencil, Trash2, Star, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { StoreCard, DeliveryCard } from "./StoreCard";
import { storeErrorKey } from "@/lib/stores";

const countryName = (config, cc, lang) => config.country_names?.[cc]?.[lang] || cc;

// Étape 2 : zone enregistrée → choix drive / livraison, rayon, résultats (confirmés vs à vérifier)
export function StoreResults({ zone, config, onChange, onEdit, onDelete }) {
  const { t, tv, lang } = useLang();
  const { data, isLoading, error } = useQuery({
    queryKey: ["stores-search", zone.lat, zone.lng, zone.country, zone.mode, zone.radius_km, zone.precision],
    queryFn: async () => (await api.post("/stores/search", { lat: zone.lat, lng: zone.lng, country: zone.country, mode: zone.mode, radius_km: zone.radius_km, precision: zone.precision, address: zone.label })).data,
    retry: false, staleTime: 5 * 60 * 1000,
  });

  const isPreferred = (retailer_id, place_id) => zone.preferred && zone.preferred.retailer_id === retailer_id && (zone.preferred.place_id || null) === (place_id || null);
  const togglePreferred = (retailer_id, place_id, name) => onChange({ preferred: isPreferred(retailer_id, place_id) ? null : { retailer_id, place_id: place_id || null, name } });

  // Résultats chargés → on amène la liste à l'écran (le haut de la zone reste visible)
  const resultsRef = useRef(null);
  useEffect(() => {
    if (!data || !resultsRef.current) return;
    const r = resultsRef.current.getBoundingClientRect();
    if (r.top < 0 || r.top > window.innerHeight * 0.6) resultsRef.current.scrollIntoView({ block: "start" });
  }, [data]);

  const Mode = ({ value, Icon, label, desc }) => (
    <button type="button" onClick={() => onChange({ mode: value })} data-testid={`store-mode-${value}`} aria-pressed={zone.mode === value}
      className={`flex items-start gap-3 rounded-2xl border p-3 text-left transition-colors ${zone.mode === value ? "border-primary bg-chenia-rosesoft/50" : "border-border bg-background/60 hover:bg-background"}`}>
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${zone.mode === value ? "text-primary" : "text-muted-foreground"}`} />
      <span><span className="block text-sm font-medium">{label}</span><span className="block text-xs text-muted-foreground">{desc}</span></span>
    </button>
  );

  return (
    <div className="mt-5 scroll-mt-20" data-testid="store-results" ref={resultsRef}>
      <div className="rounded-2xl bg-secondary/60 p-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="store-zone">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" data-testid="store-zone-label">{zone.label || `${zone.lat.toFixed(3)}, ${zone.lng.toFixed(3)}`}</p>
          <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5">
            <span>{t("st_country")} :</span>
            <select value={zone.country} onChange={(e) => onChange({ country: e.target.value })} aria-label={t("st_fix_country")} data-testid="store-zone-country"
              className="h-7 rounded-full border border-border bg-card px-2 text-xs">
              {config.countries.map((cc) => <option key={cc} value={cc}>{countryName(config, cc, lang)}</option>)}
            </select>
            {zone.country_detected && zone.country_detected !== zone.country && <span data-testid="store-zone-country-corrected">({t("st_country_detected")} : {countryName(config, zone.country_detected, lang)})</span>}
            <span>· {t(`st_precision_${zone.precision === "approximate" ? "area" : zone.precision}`)}</span>
          </p>
          {zone.preferred && (
            <p className="mt-1.5 text-xs inline-flex items-center gap-1.5 rounded-full bg-chenia-sage/40 px-2.5 py-1 max-w-full" data-testid="store-zone-preferred">
              <Star className="h-3 w-3 fill-current shrink-0" />
              <span className="truncate">{t("st_set_preferred")} : <span className="font-medium">{zone.preferred.name || zone.preferred.retailer_id}</span></span>
              <button type="button" onClick={() => onChange({ preferred: null })} aria-label={t("st_remove_preferred")} data-testid="store-zone-preferred-clear" className="shrink-0 rounded-full hover:bg-background/70 p-0.5"><X className="h-3 w-3" /></button>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 h-8 text-xs hover:bg-background" data-testid="store-zone-edit"><Pencil className="h-3 w-3" />{t("st_change_zone")}</button>
          <button type="button" onClick={onDelete} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 h-8 text-xs text-muted-foreground hover:text-chenia-terracottadark" data-testid="store-zone-delete"><Trash2 className="h-3 w-3" />{t("st_delete")}</button>
        </div>
      </div>

      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <Mode value="drive" Icon={Car} label={t("st_mode_drive")} desc={t("st_mode_drive_desc")} />
        <Mode value="delivery" Icon={Truck} label={t("st_mode_delivery")} desc={t("st_mode_delivery_desc")} />
      </div>

      {zone.mode === "drive" && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="store-radius">
          <span className="text-xs text-muted-foreground mr-1">{t("st_radius")} :</span>
          {(config.radius_options || [5, 10, 20, 30]).map((km) => (
            <button key={km} type="button" onClick={() => onChange({ radius_km: km })} data-testid={`store-radius-${km}`} aria-pressed={zone.radius_km === km}
              className={`rounded-full px-3 h-7 text-xs border transition-colors ${zone.radius_km === km ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-background"}`}>{km} {t("st_km")}</button>
          ))}
        </div>
      )}

      {isLoading && <p className="mt-4 text-sm text-muted-foreground" data-testid="store-results-loading">{t("loading")}</p>}
      {error && <p className="mt-4 text-sm text-chenia-terracottadark" data-testid="store-results-error">{t(storeErrorKey(error))}</p>}

      {data?.demo && <p className="mt-3 text-[11px] rounded-full bg-chenia-rosesoft px-3 py-1 inline-block" data-testid="store-demo-banner">{t("st_demo")}</p>}

      {data && data.mode === "drive" && <DriveResults data={data} zone={zone} isPreferred={isPreferred} togglePreferred={togglePreferred} onChange={onChange} />}
      {data && data.mode === "delivery" && <DeliveryResults data={data} isPreferred={isPreferred} togglePreferred={togglePreferred} onChange={onChange} />}

      {data?.closed_retailers?.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="store-closed-list">
          {data.closed_retailers.map((r) => (
            <li key={r.id} className="text-[11px] text-muted-foreground" data-testid={`store-closed-${r.id}`}><span className="font-medium">{r.name}</span> — {t("st_closed")}{r.note ? ` · ${tv(r.note)}` : ""}</li>
          ))}
        </ul>
      )}
      {data?.attribution && <p className="mt-3 text-[11px] text-muted-foreground" data-testid="store-attribution">{data.attribution}</p>}
    </div>
  );
}

// Menu déroulant natif (défilement et sélection fiables sur iPhone) : une seule carte, celle du magasin choisi
function StoreSelect({ options, value, onSelect, label, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold" data-testid={`${testid}-label`}>{label}</span>
      <select value={value} onChange={(e) => onSelect(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full h-11 rounded-xl border border-input bg-card px-3 text-sm">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function DriveResults({ data, zone, isPreferred, togglePreferred, onChange }) {
  const { t } = useLang();
  const stores = [...data.confirmed, ...data.to_verify];
  const empty = !stores.length;
  const nextRadius = (data.radius_options || []).find((km) => km > zone.radius_km);
  const chosen = stores.find((s) => isPreferred(s.retailer_id, s.place_id));
  const select = (place_id) => {
    const s = stores.find((x) => x.place_id === place_id);
    onChange({ preferred: s ? { retailer_id: s.retailer_id, place_id: s.place_id, name: s.name } : null });
  };
  const optLabel = (s) => `${s.name} · ${t("st_away", { km: s.distance_km })}${s.drive_status === "confirmed" ? ` · ${t("st_services_confirmed")}` : ""}`;
  return (
    <div className="mt-4" data-testid="drive-results">
      {empty && (
        <div className="rounded-2xl bg-background/60 border border-border p-4" data-testid="store-no-results">
          <p className="text-sm font-medium">{t("st_no_results")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("st_no_results_help")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {nextRadius && <button type="button" onClick={() => onChange({ radius_km: nextRadius })} className="rounded-full bg-primary text-primary-foreground px-4 h-8 text-xs" data-testid="store-widen">{t("st_widen")} · {nextRadius} {t("st_km")}</button>}
            <button type="button" onClick={() => onChange({ mode: "delivery" })} className="rounded-full border border-border bg-card px-4 h-8 text-xs" data-testid="store-switch-delivery">{t("st_mode_delivery")}</button>
          </div>
        </div>
      )}
      {!empty && (
        <>
          <StoreSelect options={stores.map((s) => ({ value: s.place_id, label: optLabel(s) }))} value={chosen?.place_id || ""} onSelect={select}
            label={t("st_choose_store")} placeholder={t("st_choose_placeholder", { n: stores.length })} testid="store-select" />
          <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="store-results-count">{t("st_results_count", { n: stores.length })} · {t("st_distance_note")}</p>
          {!data.confirmed.length && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="store-no-confirmed">{t("st_no_confirmed")}</p>}
          {chosen && (
            <ul className="mt-3" data-testid="store-chosen">
              <StoreCard store={chosen} preferred onPrefer={() => togglePreferred(chosen.retailer_id, chosen.place_id, chosen.name)} />
            </ul>
          )}
        </>
      )}
      {data.retailers_without_results?.length > 0 && !empty && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="store-not-found-for">
          {t("st_not_found_for", { names: data.retailers_without_results.map((r) => r.name).join(", ") })}
        </p>
      )}
    </div>
  );
}

function DeliveryResults({ data, isPreferred, togglePreferred, onChange }) {
  const { t } = useLang();
  const services = [...data.confirmed, ...data.to_verify];
  const chosen = services.find((s) => isPreferred(s.retailer_id, null));
  const select = (rid) => {
    const s = services.find((x) => x.retailer_id === rid);
    onChange({ preferred: s ? { retailer_id: s.retailer_id, place_id: null, name: s.retailer_name } : null });
  };
  return (
    <div className="mt-4" data-testid="delivery-results">
      {!data.address_precise && <div className="rounded-2xl bg-chenia-rosesoft/60 p-3 text-xs mb-3" data-testid="delivery-imprecise">{t("st_delivery_imprecise")}</div>}
      {services.length > 0 ? (
        <>
          <StoreSelect options={services.map((s) => ({ value: s.retailer_id, label: `${s.retailer_name} · ${s.delivery_status === "confirmed" ? t("st_services_confirmed") : t("st_to_verify")}` }))}
            value={chosen?.retailer_id || ""} onSelect={select} label={t("st_choose_service")} placeholder={t("st_choose_placeholder", { n: services.length })} testid="delivery-select" />
          {!data.confirmed.length && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="delivery-to-verify-note">{t("st_delivery_to_verify")}</p>}
          {chosen && (
            <ul className="mt-3" data-testid="delivery-chosen">
              <DeliveryCard service={chosen} preferred onPrefer={() => togglePreferred(chosen.retailer_id, null, chosen.retailer_name)} />
            </ul>
          )}
        </>
      ) : <p className="text-sm text-muted-foreground" data-testid="delivery-no-results">{t("st_no_results")}</p>}
    </div>
  );
}
